import {
  ADMIN_RESOURCE_COMMENT_PATH,
  RESOURCE_ACTIVITY_LIST_PATH,
  RESOURCE_COMMENTS_PATH,
  RESOURCE_FAQ_PATH,
  RESOURCE_FAQS_PATH,
  RESOURCE_REACTIONS_PATH,
  RESOURCE_SUBSCRIPTIONS_PATH,
} from '@/config/routes'
import API from '@/lib/API'
import { activityEnhancer } from '@/lib/helpers/modules/activityHelper'
import { buildURL } from '@/lib/helpers/urlHelpers'
import type {
  IListResourceActivitiesResponse,
  IResourceActivityItem,
} from '@/types/activity'
import type { IComment, ICreateCommentData } from '@/types/comment'
import type { IListAPIParams, IResourceTypes } from '@/types/common'
import type { ICreateFaqItem, IFaqItem } from '@/types/faq'

export type IResources = 'article' | 'changelog' | 'feature_request'

export interface IResourceBaseData {
  resourceId: string | number
  resourceModule: IResources
}
interface IListResourceSubscriberParams extends IListAPIParams {
  resource_id: string | number
  resource_module: IResources
}

export const listResourceSubscribers = (
  params: IListResourceSubscriberParams
) => {
  return API.get(RESOURCE_SUBSCRIPTIONS_PATH, params)
}

// Comments
export const getResourceComments = (
  id: string | number,
  resource_module: string,
  params: any
): Promise<IComment[]> => {
  return API.get(RESOURCE_COMMENTS_PATH, {
    ...params,
    resource_id: id,
    resource_module,
  }).then((data) => [...data.pinned_comments, ...data.unpinned_comments])
}

export const addResourceComment = (
  id: string | number,
  resourceModule: IResources,
  comment: ICreateCommentData
): Promise<IComment> => {
  const url = buildURL(RESOURCE_COMMENTS_PATH, {
    resource_id: id,
    resource_module: resourceModule,
  })
  return API.post(url, comment)
}

export const deleteResourceComment = (id: string | number) => {
  return API.delete(`${RESOURCE_COMMENTS_PATH}/${id}`)
}

export const updateResourceComment = (
  commentId: string | number,
  comment: Partial<IComment>
) => {
  return API.put(`${RESOURCE_COMMENTS_PATH}/${commentId}`, comment)
}

export const updateAdminResourceComment = (
  commentId: string | number,
  comment: Partial<IComment>
) => {
  return API.put(ADMIN_RESOURCE_COMMENT_PATH(commentId), comment)
}

export const listResourceReactions = (
  resource_id: string | number,
  resource_module: IResources
) => {
  return API.get(RESOURCE_REACTIONS_PATH, {
    resource_id,
    resource_module,
  }).then((data) => data.reactions)
}

export const listResourceReactedUsers = (
  resource_id: string | number,
  resource_module: IResources,
  reaction_id: string | number
) => {
  return API.get(`${RESOURCE_REACTIONS_PATH}/${reaction_id}`, {
    resource_id,
    resource_module,
  })
}

export const listResourceActivities = (data: {
  id?: string | number
  type?: IResourceTypes
  page?: number
}): Promise<{
  activities: IResourceActivityItem[]
}> => {
  return API.get(RESOURCE_ACTIVITY_LIST_PATH, {
    resource_type: data.type,
    resource_id: data.id,
    page: data.page || 1,
  })
    .then((act: IListResourceActivitiesResponse) => ({
      activities: Object.values(act.activities)
        .flat()
        .map((activity) => activityEnhancer(activity, act.associative_records)),
    }))
    .then(({ activities }) => {
      return { activities }
    })
}

export const listResourceFaqs = (id: string | number, type: IResourceTypes) => {
  return API.get(RESOURCE_FAQS_PATH, {
    resource_type: type,
    resource_id: id,
  })
}

export const addResourceFaq = (faq: ICreateFaqItem) => {
  return API.post(RESOURCE_FAQS_PATH, faq)
}

export const updateResourceFaq = (
  id: string | number,
  faq: Partial<IFaqItem>
) => {
  return API.put(RESOURCE_FAQ_PATH(id), faq)
}

export const deleteResourceFaq = (id: string | number) => {
  return API.delete(RESOURCE_FAQ_PATH(id))
}
